/**
 * FEAT-058 — pure helpers for the "send draft for review" dialog.
 *
 * DB/network-free so they're unit-testable directly. Two send paths share
 * the recipient list: "Send via CCM" posts to the API (branded HTML, real
 * send); "Send from my email" opens a mailto: link with a plain-text
 * excerpt — no styling survives a mailto body, this is a convenience
 * fallback for founders who'd rather send from their own inbox.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Splits on comma/semicolon/newline, trims, dedupes, drops empties. Does not validate format. */
export function parseRecipients(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(/[,;\n]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
    )
  );
}

export function isValidEmail(s: string): boolean {
  return EMAIL_RE.test(s);
}

/** Recipients from parseRecipients() that don't look like a valid email. */
export function invalidRecipients(recipients: string[]): string[] {
  return recipients.filter((r) => !isValidEmail(r));
}

/**
 * Strips HTML down to a readable plain-text excerpt for a mailto: body —
 * mail clients don't render HTML from a mailto link, so this is a lossy,
 * best-effort conversion, not the branded email.
 */
export function stripHtmlToText(html: string, maxLen = 1800): string {
  const text = html
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|h2|h3|li|blockquote)>/gi, "\n\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen).trimEnd()}…\n\n[Draft truncated for email length.]`;
}

/**
 * RFC 6068 mailto: — percent-encode manually rather than URLSearchParams,
 * which encodes spaces as "+" (form-encoding), not "%20"; mail clients
 * don't treat "+" as a space in a mailto body/subject.
 */
export function buildMailtoHref(opts: { to: string[]; subject: string; body: string }): string {
  const to = opts.to.map(encodeURIComponent).join(",");
  const subject = encodeURIComponent(opts.subject);
  const body = encodeURIComponent(opts.body);
  return `mailto:${to}?subject=${subject}&body=${body}`;
}

export interface RecipientSummary {
  /** Everything the reader typed, split. */
  all: string[];
  /** Entries that are not addresses — named so the reader can fix them. */
  invalid: string[];
  /** How many will actually receive it. */
  validCount: number;
  /** Neither send arms until this is true. */
  ready: boolean;
}

/**
 * FEAT-087 / S-373 — the handoff asks the modal to *count* the valid
 * addresses and flag the invalid ones before either send arms, rather than
 * only complaining once something is wrong. Sending to four people when you
 * meant five is not an error the app can detect afterwards, so the count is
 * shown before the send, not after.
 */
export function summariseRecipients(raw: string): RecipientSummary {
  const all = parseRecipients(raw);
  const invalid = invalidRecipients(all);
  return {
    all,
    invalid,
    validCount: all.length - invalid.length,
    ready: all.length > 0 && invalid.length === 0,
  };
}
